// Dependencies
import React, {
  Ref,
  useState,
  useRef,
  useCallback,
  useImperativeHandle,
  forwardRef,
  KeyboardEventHandler,
  useEffect,
  useMemo,
} from 'react';
import { useClickAway } from 'react-use';
import { useTheme } from 'styled-components';
import { useTranslation } from 'react-i18next';

// Types
import {
  MultiSelectDropdownProps,
  ImperativeRefMultiSelect,
  OptionSelected,
} from './types';

// Styled Components
import {
  Container,
  DropDownHeader,
  DropDownContainer,
  Label,
  ArrowContainer,
  ArrowDown,
  SelectedContent,
  Placeholder,
  Tag,
  TagLabel,
  TagRemove,
  DropDownListContainer,
  List,
  ListItem,
  Checkbox,
  OptionLabel,
  SearchInputBox,
  SearchInput,
  NoResultsText,
} from './styles';

// Components
import { ErrorMessage } from '../../InputText/styles';
import { matchesSearch } from '../../../../utils/textSearch';

const MultiSelectDropdown = (
  {
    inputSize,
    typeDropDown,
    placeholder,
    labelText,
    options,
    onChangeSelected,
    selectedValues: selectedValuesProp,
    error,
    borderError,
    isDisabled,
    searchable = false,
    searchPlaceholder,
    minSearchLength = 0,
    noResultsText,
    caseSensitive = false,
    ...props
  }: MultiSelectDropdownProps,
  innerRef: Ref<ImperativeRefMultiSelect>
): JSX.Element => {
  const theme = useTheme();
  const { t } = useTranslation();
  const [isActive, setIsActive] = useState(false);
  const [selectedValues, setSelectedValues] = useState<string[]>(
    selectedValuesProp || []
  );
  const [searchTerm, setSearchTerm] = useState('');

  const containerRef = useRef<HTMLDivElement>(null);
  const searchInputRef = useRef<HTMLInputElement>(null);

  // Sync selectedValues prop with local state
  useEffect(() => {
    if (selectedValuesProp !== undefined) {
      setSelectedValues(selectedValuesProp);
    }
  }, [selectedValuesProp]);

  // Searchable: filtrar options por searchTerm. Si searchable=false o no hay
  // término (o no se alcanza minSearchLength), devolver options sin tocar.
  // Para conjuntos pequeños (≤ ~50) no truncamos a 200 — viene del consumer.
  const displayedOptions = useMemo(() => {
    if (
      !searchable ||
      !searchTerm ||
      searchTerm.length < minSearchLength
    ) {
      return options;
    }
    // Sin mayúsculas, sin acentos y por palabras: `matchesSearch`, la misma
    // normalización que el backend. `caseSensitive` conserva la literal.
    return options.filter((opt) =>
      caseSensitive
        ? opt.label.includes(searchTerm)
        : matchesSearch(opt.label, searchTerm)
    );
  }, [options, searchable, searchTerm, minSearchLength, caseSensitive]);

  useClickAway(containerRef, () => {
    setIsActive(false);
    setSearchTerm('');
  });

  // Auto-focus del input al abrir el dropdown SOLO en desktop. En
  // móvil/tablet no enfocamos para evitar abrir el teclado virtual
  // (mismo patrón que SearchableDropdown).
  useEffect(() => {
    if (!searchable || !isActive) return;
    const isTabletOrMobile = window.matchMedia(
      `(max-width: ${
        theme.breakPoints.landscapeTablet * theme.percentBaseResponsive
      }rem)`,
    ).matches;
    if (isTabletOrMobile) return;
    // setTimeout(0) garantiza que el input ya está montado en el DOM.
    // Guardamos el id para limpiarlo si el dropdown se cierra antes de
    // que dispare (evita focus sobre un ref obsoleto).
    const id = setTimeout(() => {
      searchInputRef.current?.focus();
    }, 0);
    return () => clearTimeout(id);
  }, [
    isActive,
    searchable,
    theme.breakPoints.landscapeTablet,
    theme.percentBaseResponsive,
  ]);

  const handleSearchKeyDown: KeyboardEventHandler<HTMLInputElement> =
    useCallback((event) => {
      if (event.key === 'Escape') {
        event.preventDefault();
        setIsActive(false);
        setSearchTerm('');
        searchInputRef.current?.blur();
      }
    }, []);

  const toggleOption = useCallback(
    (value: string) => {
      const newValues = selectedValues.includes(value)
        ? selectedValues.filter((v) => v !== value)
        : [...selectedValues, value];

      setSelectedValues(newValues);
      if (onChangeSelected) {
        onChangeSelected(newValues);
      }
    },
    [selectedValues, onChangeSelected]
  );

  const removeOption = useCallback(
    (value: string, e: React.MouseEvent) => {
      e.stopPropagation();
      const newValues = selectedValues.filter((v) => v !== value);
      setSelectedValues(newValues);
      if (onChangeSelected) {
        onChangeSelected(newValues);
      }
    },
    [selectedValues, onChangeSelected]
  );

  const toggleDropdown = useCallback(() => {
    if (!isDisabled) {
      setIsActive((prev) => {
        if (prev) setSearchTerm('');
        return !prev;
      });
    }
  }, [isDisabled]);

  useImperativeHandle(innerRef, () => ({
    clearSelected: () => {
      setSelectedValues([]);
      setIsActive(false);
      if (onChangeSelected) {
        onChangeSelected([]);
      }
    },
    setSelectedValues: (values) => {
      setSelectedValues(values);
      if (onChangeSelected) {
        onChangeSelected(values);
      }
    },
  }));

  const pressFocus: KeyboardEventHandler = useCallback(
    (event) => {
      if (event.key === ' ' || event.key === 'Enter') {
        event.preventDefault();
        toggleDropdown();
      }
    },
    [toggleDropdown]
  );

  const getSelectedOptions = useCallback((): OptionSelected[] => {
    return options.filter((opt) => selectedValues.includes(opt.value));
  }, [options, selectedValues]);

  const selectedOptions = getSelectedOptions();

  return (
    <Container
      inputSize={inputSize}
      labelText={!!labelText}
      typeDropDown={typeDropDown}
      {...props}
    >
      {labelText && <Label colorText="black">{labelText}</Label>}
      <DropDownContainer ref={containerRef}>
        <DropDownHeader
          typeDropDown={typeDropDown}
          isActive={isActive}
          borderError={borderError}
          tabIndex={0}
          onKeyDownCapture={pressFocus}
          isDisabled={isDisabled}
          onClick={(e) => {
            e.stopPropagation();
            toggleDropdown();
          }}
        >
          <SelectedContent>
            {selectedOptions.length > 0 ? (
              selectedOptions.map((option) => (
                <Tag key={option.value} title={option.label}>
                  <TagLabel>{option.label}</TagLabel>
                  <TagRemove
                    onClick={(e) => removeOption(option.value, e)}
                    type="button"
                    aria-label={`Quitar ${option.label}`}
                  >
                    ×
                  </TagRemove>
                </Tag>
              ))
            ) : (
              <Placeholder variant="regular">{placeholder}</Placeholder>
            )}
          </SelectedContent>

          <ArrowContainer
            onClick={(e) => {
              e.stopPropagation();
              toggleDropdown();
            }}
          >
            <ArrowDown />
          </ArrowContainer>
        </DropDownHeader>

        {isActive && (
          <DropDownListContainer>
            <List role="listbox">
              {searchable && (
                <SearchInputBox onClick={(e) => e.stopPropagation()}>
                  <SearchInput
                    ref={searchInputRef}
                    type="search"
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    onKeyDown={handleSearchKeyDown}
                    onClick={(e) => e.stopPropagation()}
                    placeholder={
                      searchPlaceholder ?? t('common:buttons.search')
                    }
                    aria-label={
                      searchPlaceholder ?? t('common:buttons.search')
                    }
                  />
                </SearchInputBox>
              )}
              {displayedOptions.length === 0 && searchable && searchTerm ? (
                <NoResultsText>
                  {noResultsText ?? t('common:messages.noResults')}
                </NoResultsText>
              ) : (
                displayedOptions.map((option, index) => (
                  <ListItem
                    key={option.value}
                    isSelected={selectedValues.includes(option.value)}
                    tabIndex={0}
                    role="option"
                    aria-selected={selectedValues.includes(option.value)}
                    onClick={() => toggleOption(option.value)}
                    onKeyDown={(event) => {
                      // Space toggle (estándar ARIA para role="option").
                      // Enter NO toggle: queremos que bubble al
                      // useFilterKeyboardShortcuts del contenedor padre y
                      // dispare la búsqueda preservando la selección actual.
                      if (event.key === ' ') {
                        event.preventDefault();
                        toggleOption(option.value);
                      }
                    }}
                    onBlur={() => {
                      if (index === displayedOptions.length - 1) {
                        // Optional: close on last item blur
                      }
                    }}
                  >
                    <Checkbox
                      checked={selectedValues.includes(option.value)}
                      onChange={() => {}}
                      /* preventDefault evita el toggle nativo de <input> —
                         dejamos que el click bubble al ListItem.onClick que
                         llama toggleOption (controlled). Sin esto, click
                         directo en checkbox no hacía nada porque el
                         stopPropagation anterior bloqueaba el dispatch del
                         ListItem y el onChange no-op no actualizaba state. */
                      onClick={(e) => e.preventDefault()}
                    />
                    {/* `title`: la etiqueta se recorta con puntos
                        suspensivos cuando no cabe, así que el texto completo
                        tiene que seguir estando a mano. Mismo patrón que
                        `MaterialNameCell` en la tabla de Gestión. */}
                    <OptionLabel title={option.label}>
                      {option.label}
                    </OptionLabel>
                  </ListItem>
                ))
              )}
            </List>
          </DropDownListContainer>
        )}
      </DropDownContainer>
      {error && (
        <ErrorMessage colorText={'lightCoralRed'} size={'XXS'}>
          {error}
        </ErrorMessage>
      )}
    </Container>
  );
};

export default forwardRef(MultiSelectDropdown);
